import { useEffect, useState } from 'react';
import { CreditCard, Save, AlertCircle, CheckCircle, ExternalLink } from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface PlanRow {
  slug: 'starter' | 'growth' | 'pro' | 'scale';
  name: string;
  description: string | null;
  tier_level: number;
  monthly_price_cents: number;
  annual_price_cents: number;
  stripe_product_id: string | null;
  stripe_price_id_monthly: string | null;
  stripe_price_id_annual: string | null;
  features: Record<string, unknown>;
  active: boolean;
  updated_at: string;
}

interface PlansResponse {
  plans: PlanRow[];
}

interface SaveResponse {
  plan: PlanRow;
  stripe_sync: {
    stripe_product_id: string;
    stripe_price_id_monthly: string;
    price_changed: boolean;
    product_changed: boolean;
  } | null;
  stripe_error: string | null;
}

interface EditState {
  name: string;
  description: string;
  monthly_price_dollars: string;
  tier_level: number;
  features_json: string;
  active: boolean;
}

function planToEditState(p: PlanRow): EditState {
  return {
    name: p.name,
    description: p.description ?? '',
    monthly_price_dollars: (p.monthly_price_cents / 100).toFixed(2),
    tier_level: p.tier_level,
    features_json: JSON.stringify(p.features ?? {}, null, 2),
    active: p.active,
  };
}

interface AddonRow {
  slug: string;
  name: string;
  description: string | null;
  monthly_price_cents: number;
  annual_price_cents: number;
  stripe_product_id: string | null;
  stripe_price_id_monthly: string | null;
  stripe_price_id_annual: string | null;
  intro_percent_off: number | null;
  intro_duration_months: number | null;
  features: Record<string, unknown>;
  active: boolean;
  updated_at: string;
}

interface AddonsResponse {
  addons: AddonRow[];
}

interface AddonSaveResponse {
  addon: AddonRow;
  stripe_sync: {
    stripe_product_id: string;
    stripe_price_id_monthly: string;
    stripe_price_id_annual: string;
    price_changed: boolean;
    product_changed: boolean;
  } | null;
  stripe_error: string | null;
}

interface AddonEditState {
  name: string;
  description: string;
  monthly_price_dollars: string;
  annual_price_dollars: string;
  intro_percent_off: string;
  intro_duration_months: string;
  features_json: string;
  active: boolean;
}

function addonToEditState(a: AddonRow): AddonEditState {
  return {
    name: a.name,
    description: a.description ?? '',
    monthly_price_dollars: (a.monthly_price_cents / 100).toFixed(2),
    annual_price_dollars: (a.annual_price_cents / 100).toFixed(2),
    intro_percent_off: a.intro_percent_off != null ? String(a.intro_percent_off) : '',
    intro_duration_months: a.intro_duration_months != null ? String(a.intro_duration_months) : '',
    features_json: JSON.stringify(a.features ?? {}, null, 2),
    active: a.active,
  };
}

export default function PlatformPlans() {
  const [plans, setPlans] = useState<PlanRow[]>([]);
  const [edits, setEdits] = useState<Record<string, EditState>>({});
  const [addons, setAddons] = useState<AddonRow[]>([]);
  const [addonEdits, setAddonEdits] = useState<Record<string, AddonEditState>>({});
  const [loading, setLoading] = useState(true);
  const [savingSlug, setSavingSlug] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState<{ slug: string; msg: string } | null>(null);

  async function loadPlans() {
    try {
      const res = await api.get<PlansResponse>('/admin/platform/plans');
      setPlans(res.plans);
      const next: Record<string, EditState> = {};
      for (const p of res.plans) next[p.slug] = planToEditState(p);
      setEdits(next);
      const addonRes = await api.get<AddonsResponse>('/admin/platform/addons');
      setAddons(addonRes.addons);
      const nextAddons: Record<string, AddonEditState> = {};
      for (const a of addonRes.addons) nextAddons[a.slug] = addonToEditState(a);
      setAddonEdits(nextAddons);
    } catch (err: any) {
      setError(err.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadPlans();
  }, []);

  function updateEdit(slug: string, patch: Partial<EditState>) {
    setEdits((prev) => ({ ...prev, [slug]: { ...prev[slug], ...patch } }));
  }

  function updateAddonEdit(slug: string, patch: Partial<AddonEditState>) {
    setAddonEdits((prev) => ({ ...prev, [slug]: { ...prev[slug], ...patch } }));
  }

  async function handleSaveAddon(slug: string) {
    const edit = addonEdits[slug];
    if (!edit) return;

    let featuresParsed: Record<string, unknown>;
    try {
      featuresParsed = JSON.parse(edit.features_json);
      if (typeof featuresParsed !== 'object' || featuresParsed === null || Array.isArray(featuresParsed)) {
        throw new Error('Features must be a JSON object');
      }
    } catch (err: any) {
      setError(`Features JSON invalid for ${slug}: ${err.message}`);
      return;
    }

    const monthlyCents = Math.round(parseFloat(edit.monthly_price_dollars) * 100);
    const annualCents = Math.round(parseFloat(edit.annual_price_dollars) * 100);
    if (!Number.isFinite(monthlyCents) || monthlyCents < 0 || !Number.isFinite(annualCents) || annualCents < 0) {
      setError(`Prices must be non-negative numbers (add-on: ${slug})`);
      return;
    }
    const introPercent = edit.intro_percent_off.trim() === '' ? null : parseInt(edit.intro_percent_off, 10);
    const introMonths = edit.intro_duration_months.trim() === '' ? null : parseInt(edit.intro_duration_months, 10);
    if ((introPercent == null) !== (introMonths == null)) {
      setError(`Intro % and intro months must be set together (add-on: ${slug})`);
      return;
    }

    const current = addons.find((a) => a.slug === slug);
    const priceChanging =
      current && (current.monthly_price_cents !== monthlyCents || current.annual_price_cents !== annualCents);
    if (priceChanging) {
      const ok = window.confirm(
        `Changing the price for "${current!.name}" will create new Stripe Prices and archive the old ones. ` +
        `Existing subscriptions continue at the old price; new sign-ups will use the new one. Proceed?`,
      );
      if (!ok) return;
    }

    setSavingSlug(slug);
    setError('');
    try {
      const res = await api.put<AddonSaveResponse>(`/admin/platform/addons/${slug}`, {
        name: edit.name.trim(),
        description: edit.description.trim() || null,
        monthly_price_cents: monthlyCents,
        annual_price_cents: annualCents,
        intro_percent_off: introPercent,
        intro_duration_months: introMonths,
        features: featuresParsed,
        active: edit.active,
      });

      let msg = 'Saved.';
      if (res.stripe_sync) {
        const parts: string[] = [];
        if (res.stripe_sync.product_changed) parts.push('Stripe Product updated');
        if (res.stripe_sync.price_changed) parts.push('new Stripe Price(s) created');
        msg = parts.length ? `Saved — ${parts.join(', ')}.` : 'Saved (Stripe in sync).';
      } else if (res.stripe_error) {
        msg = `Saved DB only — Stripe sync failed: ${res.stripe_error}`;
      }
      setSuccessMessage({ slug, msg });
      setTimeout(() => setSuccessMessage(null), 5000);
      await loadPlans();
    } catch (err: any) {
      setError(err.message || 'Save failed');
    } finally {
      setSavingSlug(null);
    }
  }

  async function handleSave(slug: string) {
    const edit = edits[slug];
    if (!edit) return;

    // Validate features JSON before we ship it.
    let featuresParsed: Record<string, unknown>;
    try {
      featuresParsed = JSON.parse(edit.features_json);
      if (typeof featuresParsed !== 'object' || featuresParsed === null || Array.isArray(featuresParsed)) {
        throw new Error('Features must be a JSON object');
      }
    } catch (err: any) {
      setError(`Features JSON invalid for ${slug}: ${err.message}`);
      return;
    }

    const monthlyCents = Math.round(parseFloat(edit.monthly_price_dollars) * 100);
    if (!Number.isFinite(monthlyCents) || monthlyCents < 0) {
      setError(`Monthly price must be a non-negative number (slug: ${slug})`);
      return;
    }

    const current = plans.find((p) => p.slug === slug);
    const priceChanging = current && current.monthly_price_cents !== monthlyCents;
    if (priceChanging) {
      const ok = window.confirm(
        `Changing the price for "${current!.name}" will create a new Stripe Price and archive the old one. ` +
        `Existing subscriptions continue at the old price; new sign-ups will use the new one. Proceed?`,
      );
      if (!ok) return;
    }

    setSavingSlug(slug);
    setError('');
    try {
      const res = await api.put<SaveResponse>(`/admin/platform/plans/${slug}`, {
        name: edit.name.trim(),
        description: edit.description.trim() || null,
        monthly_price_cents: monthlyCents,
        tier_level: edit.tier_level,
        features: featuresParsed,
        active: edit.active,
      });

      let msg = 'Saved.';
      if (res.stripe_sync) {
        const parts: string[] = [];
        if (res.stripe_sync.product_changed) parts.push('Stripe Product updated');
        if (res.stripe_sync.price_changed) parts.push('new Stripe Price created');
        msg = parts.length ? `Saved — ${parts.join(', ')}.` : 'Saved (Stripe in sync).';
      } else if (res.stripe_error) {
        msg = `Saved DB only — Stripe sync failed: ${res.stripe_error}`;
      }
      setSuccessMessage({ slug, msg });
      setTimeout(() => setSuccessMessage(null), 5000);
      await loadPlans();
    } catch (err: any) {
      setError(err.message || 'Save failed');
    } finally {
      setSavingSlug(null);
    }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
            <CreditCard className="w-6 h-6" />
            Platform Plans
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Customer-facing pricing for self-serve Rumio site provisioning.
            Saves sync to Stripe — first save creates the Product + Price; later
            price changes create a new Price and archive the old one.
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}

      {plans.map((plan) => {
        const edit = edits[plan.slug];
        if (!edit) return null;
        return (
          <Card key={plan.slug}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-text-primary">{plan.name}</h2>
                <p className="text-xs text-text-muted font-mono">slug: {plan.slug}</p>
              </div>
              {successMessage?.slug === plan.slug && (
                <div className="flex items-center gap-1 text-sm text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  {successMessage.msg}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Name</label>
                <Input
                  value={edit.name}
                  onChange={(e) => updateEdit(plan.slug, { name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">
                  Monthly price (USD)
                </label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={edit.monthly_price_dollars}
                  onChange={(e) => updateEdit(plan.slug, { monthly_price_dollars: e.target.value })}
                />
              </div>
              <div className="lg:col-span-2">
                <label className="block text-xs font-medium text-text-secondary mb-1">Description</label>
                <Input
                  value={edit.description}
                  onChange={(e) => updateEdit(plan.slug, { description: e.target.value })}
                  placeholder="One-line pitch for this tier"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Tier level</label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={edit.tier_level}
                  onChange={(e) => updateEdit(plan.slug, { tier_level: parseInt(e.target.value || '1', 10) })}
                />
              </div>
              <div className="flex items-end gap-2">
                <label className="inline-flex items-center gap-2 text-sm text-text-primary">
                  <input
                    type="checkbox"
                    checked={edit.active}
                    onChange={(e) => updateEdit(plan.slug, { active: e.target.checked })}
                  />
                  Active (visible on Pricing page)
                </label>
              </div>
              <div className="lg:col-span-2">
                <label className="block text-xs font-medium text-text-secondary mb-1">
                  Features (JSON object)
                </label>
                <textarea
                  className="w-full font-mono text-xs px-3 py-2 rounded-lg border border-stone-300 focus:outline-none focus:ring-2 focus:ring-sage-400 min-h-[120px]"
                  value={edit.features_json}
                  onChange={(e) => updateEdit(plan.slug, { features_json: e.target.value })}
                />
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-stone-100 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-text-muted">
              <div>
                <span className="font-medium text-text-secondary">Stripe Product:</span>{' '}
                {plan.stripe_product_id ? (
                  <a
                    href={`https://dashboard.stripe.com/products/${plan.stripe_product_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-sage-700 hover:underline inline-flex items-center gap-1"
                  >
                    {plan.stripe_product_id}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <span className="italic">none yet — will be created on first save</span>
                )}
              </div>
              <div>
                <span className="font-medium text-text-secondary">Stripe Price:</span>{' '}
                {plan.stripe_price_id_monthly ? (
                  <span className="font-mono">{plan.stripe_price_id_monthly}</span>
                ) : (
                  <span className="italic">none yet</span>
                )}
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <Button onClick={() => handleSave(plan.slug)} disabled={savingSlug === plan.slug}>
                <Save className="w-4 h-4 mr-2" />
                {savingSlug === plan.slug ? 'Saving…' : 'Save & Sync to Stripe'}
              </Button>
            </div>
          </Card>
        );
      })}

      {addons.length > 0 && (
        <div className="pt-2">
          <h2 className="text-xl font-bold text-text-primary">Partner products</h2>
          <p className="text-sm text-text-secondary mt-1">
            Products from preferred partners (Axiss Support, eTaxEngine) resold
            at the Rumio member rate alongside a hosting plan in the same
            checkout. The intro discount applies only to the partner line
            items — all active partner products must share the same intro
            terms. Standalone list prices live on each partner's own site.
          </p>
        </div>
      )}

      {addons.map((addon) => {
        const edit = addonEdits[addon.slug];
        if (!edit) return null;
        return (
          <Card key={addon.slug}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-text-primary">{addon.name}</h2>
                <p className="text-xs text-text-muted font-mono">partner product: {addon.slug}</p>
              </div>
              {successMessage?.slug === addon.slug && (
                <div className="flex items-center gap-1 text-sm text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  {successMessage.msg}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Name</label>
                <Input
                  value={edit.name}
                  onChange={(e) => updateAddonEdit(addon.slug, { name: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">
                    Monthly (USD)
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={edit.monthly_price_dollars}
                    onChange={(e) => updateAddonEdit(addon.slug, { monthly_price_dollars: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">
                    Annual (USD)
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={edit.annual_price_dollars}
                    onChange={(e) => updateAddonEdit(addon.slug, { annual_price_dollars: e.target.value })}
                  />
                </div>
              </div>
              <div className="lg:col-span-2">
                <label className="block text-xs font-medium text-text-secondary mb-1">Description</label>
                <Input
                  value={edit.description}
                  onChange={(e) => updateAddonEdit(addon.slug, { description: e.target.value })}
                  placeholder="One-line pitch shown next to the checkbox at checkout"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">
                    Intro discount %
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={edit.intro_percent_off}
                    onChange={(e) => updateAddonEdit(addon.slug, { intro_percent_off: e.target.value })}
                    placeholder="e.g. 50 (blank = none)"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">
                    Intro months
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="36"
                    value={edit.intro_duration_months}
                    onChange={(e) => updateAddonEdit(addon.slug, { intro_duration_months: e.target.value })}
                    placeholder="e.g. 12"
                  />
                </div>
              </div>
              <div className="flex items-end gap-2">
                <label className="inline-flex items-center gap-2 text-sm text-text-primary">
                  <input
                    type="checkbox"
                    checked={edit.active}
                    onChange={(e) => updateAddonEdit(addon.slug, { active: e.target.checked })}
                  />
                  Active (offered at checkout)
                </label>
              </div>
              <div className="lg:col-span-2">
                <label className="block text-xs font-medium text-text-secondary mb-1">
                  Features (JSON object — product_plan is the tier pushed to the product app)
                </label>
                <textarea
                  className="w-full font-mono text-xs px-3 py-2 rounded-lg border border-stone-300 focus:outline-none focus:ring-2 focus:ring-sage-400 min-h-[120px]"
                  value={edit.features_json}
                  onChange={(e) => updateAddonEdit(addon.slug, { features_json: e.target.value })}
                />
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-stone-100 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-text-muted">
              <div>
                <span className="font-medium text-text-secondary">Stripe Product:</span>{' '}
                {addon.stripe_product_id ? (
                  <a
                    href={`https://dashboard.stripe.com/products/${addon.stripe_product_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-sage-700 hover:underline inline-flex items-center gap-1"
                  >
                    {addon.stripe_product_id}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <span className="italic">none yet — will be created on first save</span>
                )}
              </div>
              <div>
                <span className="font-medium text-text-secondary">Monthly Price:</span>{' '}
                {addon.stripe_price_id_monthly ? (
                  <span className="font-mono">{addon.stripe_price_id_monthly}</span>
                ) : (
                  <span className="italic">none yet</span>
                )}
              </div>
              <div>
                <span className="font-medium text-text-secondary">Annual Price:</span>{' '}
                {addon.stripe_price_id_annual ? (
                  <span className="font-mono">{addon.stripe_price_id_annual}</span>
                ) : (
                  <span className="italic">none yet</span>
                )}
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <Button onClick={() => handleSaveAddon(addon.slug)} disabled={savingSlug === addon.slug}>
                <Save className="w-4 h-4 mr-2" />
                {savingSlug === addon.slug ? 'Saving…' : 'Save & Sync to Stripe'}
              </Button>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
